import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/contact')({
  component: ContactUsPage,
});

type PageState = 'loading' | 'open' | 'fallback';

function ContactUsPage() {
  const [pageState, setPageState] = useState<PageState>('loading');

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 20; // 20 attempts * 150ms = 3 seconds max
    const interval = 150;

    const tryOpenChatwoot = () => {
      if (window.$chatwoot) {
        window.$chatwoot.toggle('open');
        window.$chatwoot.toggleBubbleVisibility('hide');
        return true;
      }
      return false;
    };

    // Try immediately
    if (tryOpenChatwoot()) {
      setPageState('open');
      return;
    }

    // Poll for Chatwoot availability
    const pollInterval = setInterval(() => {
      attempts++;
      if (tryOpenChatwoot()) {
        clearInterval(pollInterval);
        setPageState('open');
      } else if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        setPageState('fallback');
      }
    }, interval);

    return () => {
      clearInterval(pollInterval);
      // Show bubble again when leaving the page
      if (window.$chatwoot) {
        window.$chatwoot.toggleBubbleVisibility('show');
      }
    };
  }, []);

  return (
    <Box
      sx={{
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 3,
      }}
    >
      {pageState === 'loading' && (
        <>
          <CircularProgress sx={{ mb: 3 }} />
          <Typography variant="h4" sx={{ mb: 2 }}>
            Opening chat...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Our chat window should open automatically. If it doesn't, please click
            the chat icon in the bottom right corner.
          </Typography>
        </>
      )}
      {pageState === 'open' && null}
      {pageState === 'fallback' && (
        <>
          <Typography variant="h4" sx={{ mb: 2 }}>
            Chat unavailable
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We couldn't open the chat widget. Please use our contact form instead.
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/contact-us"
          >
            Go to Contact Page
          </Button>
        </>
      )}
    </Box>
  );
}
